<script setup lang="ts">
import { useField } from 'vee-validate'
import { useSlots, computed } from 'vue'

const $slots = useSlots()

const fieldName = inject<string>('name', '')
const { setValue, handleChange, handleBlur } = useField(() => fieldName)

const id = useId()

const model = defineModel<string | number | Array<any> | object | boolean>()

const props = defineProps({
  placeholder: {
    type: String,
    default: null,
  },

  isFirstOptionEmpty: {
    type: Boolean,
    default: true,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  isDisabled: {
    type: Boolean,
    default: false,
  },

  isSmall: {
    type: Boolean,
    default: false,
  },

  options: {
    type: Array,
    default: () => [],
  },

  // When value is empty we show the placeholder text
  optionEmptyValue: {
    type: [String, Number, Array, Object],
    default: null,
  },

  optionValueKey: {
    type: String,
    default: 'value',
  },

  optionLabelKey: {
    type: String,
    default: 'label',
  },

  optionGroupKey: {
    type: String,
    default: 'group',
  },

  optionDisabledKey: {
    type: String,
    default: 'disabled',
  },

  loadingText: {
    type: String,
    default: '...',
  },

  isGrouped: {
    type: Boolean,
    default: false,
  },

  // Overrides prefixIcon
  prefixText: {
    type: String,
    default: null,
  },

  prefixGrouped: {
    type: Boolean,
    default: false,
  },

  prefixIcon: {
    type: String,
    default: null,
  },

  suffixText: {
    type: String,
    default: null,
  },

  suffixIcon: {
    type: String,
    default: null,
  },

  // Do not use this directly, use MultiSelect instead
  multiple: {
    type: Boolean,
    default: false,
  },

  isTransparent: {
    type: Boolean,
    default: false,
  },

  textIsTransparent: {
    type: Boolean,
    default: false,
  },

  // Vue 3 upgrade pain. If Input is nested inside another component that has it's own <Field /> parent
  // then we need to stop updateValue being called twice
  shouldSyncFormValue: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue', 'blur', 'click', 'change', 'focus'])

const options = computed(() => {
  if (props.options.length > 0) {
    // This allows you to pass a looser flat array from an API e.g ['Yes', 'No'] or [5, 9]
    if (typeof props.options[0] === 'string' || typeof props.options[0] === 'number') {
      return props.options.map((option) => {
        return {
          [this.optionLabelKey]: option,
          [this.optionValueKey]: option,
          disabled: this.optionDisabledKey,
        }
      })
    } else {
      return props.options
    }
  }

  return []
})

// If our grouped options have more than 1 unique group value then return true
const hasGroups = computed(() => {
  // It's > 2 to take into account our default group
  return Object.keys(groupedOptions.value).length > 2
})

// We need a default group in case some options have groups and others don't
const groupedOptions = computed(() => {
  // We can't group flat options
  if (!props.isGrouped || typeof props.options[0] === 'string' || typeof props.options[0] === 'number') {
    return {}
  }

  const groups = { default: [] }

  props.options.forEach((option) => {
    if (props.optionGroupKey in option) {
      if (option[props.optionGroupKey] in groups) {
        groups[option[props.optionGroupKey]].push(option)
      } else {
        groups[option[props.optionGroupKey]] = [option]
      }
    } else {
      groups.default.push(option)
    }
  })

  return groups
})

const isSelectLoading = computed(() => {
  return options.value.length === 0 || props.isLoading
})

const isDisabled = computed(() => {
  return isSelectLoading.value || props.isDisabled
})

const isEmpty = computed(() => {
  if (typeof model.value === 'object') {
    return JSON.stringify(model.value) === JSON.stringify(props.optionEmptyValue) && props.isFirstOptionEmpty
  } else {
    return model.value === props.optionEmptyValue && props.isFirstOptionEmpty
  }
})

const hasPrefix = computed(() => {
  return props.prefixIcon || props.prefixText || $slots.prefix
})

const shouldSetInitialValue = computed(() => {
  return !props.isFirstOptionEmpty && !model.value && !isSelectLoading.value
})

watch(isSelectLoading, () => setInitialValue())
watch(model, (newValue, currentValue) => {
  if (props.shouldSyncFormValue && newValue !== currentValue) {
    setValue(newValue)
  }
})

// If there are no empty options we need to set a value
function setInitialValue() {
  // Next tick to ensure options are available
  nextTick(() => {
    if (shouldSetInitialValue.value && options.value.length > 0) {
      model.value = hasGroups.value
        ? props.options[0][props.optionValueKey]
        : options.value[0][props.optionValueKey]
    }
  })
}

function onChange(event: Event) {
  const target = event.target as HTMLSelectElement

  emit('change', target?.value)
  handleChange(target?.value)
}

function onBlur() {
  emit('blur')
  handleBlur()
}
</script>

<template>
  <div
    class="flex select-wrapper"
    :class="{ transparent: isTransparent }"
  >
    <div
      v-if="hasPrefix"
      class="prefix"
      :class="[
        isDisabled ? 'text-gray-300' : 'text-gray-500',
        { overlay: !prefixGrouped, grouped: prefixGrouped },
      ]"
    >
      <!-- @slot If you need more control than passing prefixIcon -->
      <slot name="prefix">
        <p v-if="prefixText">
          {{ prefixText }}
        </p>

        <Icon
          v-else-if="prefixIcon"
          :icon="prefixIcon"
        />
      </slot>
    </div>

    <select
      v-model="model"
      :multiple="multiple"
      class="select-input"
      :class="[
        {
          'pl-10': hasPrefix && !prefixGrouped,
          'empty': isEmpty,
          '!text-transparent': textIsTransparent,
          'small': isSmall,
        },
        prefixGrouped ? 'rounded-none rounded-input-r' : 'rounded-input',
      ]"
      :disabled="isDisabled"
      @click="emit('click', $event.target)"
      @change="onChange"
      @blur="onBlur"
      @focus="emit('focus')"
    >
      <option
        v-if="isSelectLoading"
        :value="optionEmptyValue"
      >
        <slot name="loading">
          {{ loadingText }}
        </slot>
      </option>

      <option
        v-else-if="isFirstOptionEmpty && isEmpty"
        :value="optionEmptyValue"
      >
        {{ placeholder ? placeholder : $t('pleaseSelect') }}
      </option>

      <slot
        name="default"
        :options="options"
      >
        <template v-if="hasGroups">
          <optgroup
            v-for="(group, name) in groupedOptions"
            :key="name"
            :label="name !== 'default' ? name.toUpperCase() : undefined"
          >
            <option
              v-for="(option, index) in group"
              :key="`${id}-${index}`"
              :value="option[optionValueKey]"
              :disabled="option[optionDisabledKey]"
            >
              <slot
                name="option"
                :option="option"
              >
                {{ option[optionLabelKey] }}
              </slot>
            </option>
          </optgroup>
        </template>

        <template v-else>
          <option
            v-for="(option, index) in options"
            :key="`${id}-${index}`"
            :value="option[optionValueKey]"
            :disabled="option[optionDisabledKey]"
          >
            <slot
              name="option"
              :option="option"
            >
              {{ option[optionLabelKey] }}
            </slot>
          </option>
        </template>
      </slot>
    </select>
  </div>
</template>

<style lang="postcss" scoped>
.select-wrapper {
  @apply relative flex-auto shadow-sm;
}

.select-wrapper.transparent {
  @apply bg-transparent shadow-none;
}

.select-input {
  @apply block w-full py-2 pr-8 text-sm placeholder-gray-400 border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 z-1 disabled:bg-gray-50 disabled:text-gray-400;
}

.select-input.small {
  @apply py-1 pr-6 pl-2;
}

.select-input.empty {
  @apply text-gray-400;
}

.error .select-input {
  @apply text-red-900 placeholder-red-300 border-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500;
}

.select-wrapper.transparent .select-input {
  @apply bg-transparent border-none focus:ring-0;
}

.prefix.overlay {
  @apply absolute inset-y-0 left-0 flex items-center pl-3 text-sm pointer-events-none z-layer-sticker;
}

.prefix.grouped {
  @apply inline-flex items-center px-3 text-sm text-gray-500 border border-r-0 border-gray-300 rounded-l-md bg-gray-50;
}
</style>
